import React, { useCallback, useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import MathContent from '../../../math-content';
import { Container, Badge, Value } from '../';
import DefaultIcon from '../../assets/fishEyeArrow.svg?react';

export const ListedContainer = styled(Container)`
    box-shadow: var(--shadow-1);
    fill: var(--blue500);

    &:hover {
        border: 1px solid var(--gray800);
        box-shadow: var(--shadow-2);
        fill: var(--blue700);
        color: var(--gray800);
        ${(props) =>
            !props.disabled &&
            css`
                background-color: var(--gray50);
            `}
    }

    &[disabled] {
        pointer-events: none;
        box-shadow: none;
        border: 1px solid var(--gray200);
        cursor: initial;
        fill: var(--gray200);
    }

    &:active {
        box-shadow: var(--shadow-4);
        border: 1px solid var(--blue400);
        fill: var(--blue400);
    }

    ${(props) =>
        props.selected &&
        css`
            border: 1px solid var(--blue400);
            box-shadow: var(--shadow-3);
            color: var(--blue500);
            background-color: var(--gray50);
        `}

    ${(props) =>
        props.isDragging &&
        css`
            border: 1px dashed var(--blue400) !important;
            box-shadow: none !important;
        `}

    ${(props) =>
        props.disabled &&
        css`
            color: var(--gray700);
        `}
`;

export const ListedBadge = styled(Badge)`
    font-size: 16px;
    font-weight: 600;
    letter-spacing: 0px;
    line-height: 24px;
    margin-left: 0;
    text-transform: uppercase;

    & > svg {
        width: 18px;
        height: 18px;
    }

    ${(props) =>
        props.disabled &&
        css`
            background-color: var(--gray50);

            ${ListedContainer}:hover & {
                background-color: var(--gray50);
            }
            pointer-events: none;
        `}
`;

const ListedChip = ({ badgeIndex, disabled = false, handleClick, id, scrolling, selected = false, value, isDragging }) => {
    const ref = useRef(null);
    const [mathRendered, setMathRendered] = useState(false);
    useEffect(() => {
        scrolling && scrolling(ref, mathRendered);
        return () => {};
    }, [scrolling, mathRendered]);
    const onRendered = useCallback(() => setMathRendered(true), []);

    return (
        <ListedContainer
            disabled={disabled}
            id={id}
            onClick={handleClick}
            selected={selected}
            tabIndex={disabled ? -1 : 0}
            isDragging={isDragging}
        >
            <ListedBadge disabled={disabled}>{badgeIndex ? badgeIndex : <DefaultIcon />}</ListedBadge>
            <Value ref={ref} onScroll={() => scrolling(ref)} badgeIndex={badgeIndex}>
                <MathContent onRendered={onRendered}>{value}</MathContent>
            </Value>
        </ListedContainer>
    );
};

ListedChip.propTypes = {
    badgeIndex: PropTypes.string,
    disabled: PropTypes.bool,
    handleClick: PropTypes.func,
    id: PropTypes.string,
    scrolling: PropTypes.func,
    selected: PropTypes.bool,
    value: PropTypes.string,
    isDragging: PropTypes.bool,
};

export default ListedChip;
